"use client";

import React, { useState, useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Building2, Search, Handshake, FileCheck } from "lucide-react";
const stages = [{
  id: 1,
  title: "Diagnose",
  description: "Deep system scan to identify hardware failures, software conflicts, and performance bottlenecks.",
  icon: Search
}, {
  id: 2,
  title: "Repair",
  description: "Precision hardware replacement and system restoration using high-grade components and tools.",
  icon: Building2
}, {
  id: 3,
  title: "Optimize",
  description: "Fine-tuning OS parameters, thermal management, and driver updates for peak efficiency.",
  icon: FileCheck
}, {
  id: 4,
  title: "Return",
  description: "Final quality assurance testing and secure delivery of your fully optimized machine.",
  icon: Handshake
}];
function ProcessNode({
  stage,
  index,
  isLast
}: {
  stage: typeof stages[0];
  index: number;
  isLast: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };
  const Icon = stage.icon;
  return <div data-index={index} className="relative flex flex-row md:flex-col items-start md:items-center group flex-1 w-full md:w-auto gap-4 sm:gap-6 md:gap-0" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {}
      {!isLast && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-[-24px] w-0.5 bg-primary/20 md:hidden z-0" />}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg bg-background border-2 border-primary text-primary flex items-center justify-center z-10 relative shadow-md transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110 group-hover:shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" aria-label={`Stage ${stage.id}: ${stage.title}`}>
            <span className="font-default font-bold text-base sm:text-lg tracking-widest">{stage.id}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="max-w-sm md:max-w-md p-6 text-left bg-background/90 backdrop-blur-xl border-primary/30 shadow-2xl rounded-lg" side="top" sideOffset={24} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-secondary/20 rounded-lg text-secondary">
              <Icon size={20} />
            </div>
            <h4 className="font-default font-semibold text-lg text-foreground uppercase tracking-wider">{stage.title}</h4>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed font-sans">{stage.description}</p>
        </PopoverContent>
      </Popover>

      {}
      <div className="md:mt-6 pt-1 md:pt-0 flex flex-col md:items-center text-left md:text-center w-full max-w-xs sm:max-w-sm">
        <span className="font-default font-semibold text-sm sm:text-base text-secondary mb-1 uppercase tracking-widest">{stage.title}</span>
        <span className="text-xs sm:text-sm text-muted-foreground md:hidden font-sans">{stage.description}</span>
      </div>
    </div>;
}
export default function NossaMetodologia() {
  return <section data-section-id="222094" id="repair-sequence" className="py-24 md:py-32 bg-background text-foreground relative overflow-hidden scroll-mt-20">
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
      
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 max-w-6xl relative z-10">
        
        {}
        <div className="text-center mb-16 md:mb-24">
          <h2 className="font-default text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-widest uppercase">
            Repair Sequence
          </h2>
          <p className="font-sans text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            A systematic protocol designed to restore your hardware to peak operational status with surgical precision.
          </p>
        </div>

        {}
        <div className="relative w-full max-w-5xl mx-auto">
          {}
          <div className="absolute top-6 left-[5%] right-[5%] h-0.5 bg-primary/20 hidden md:block z-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary to-transparent animate-[pulse_2s_infinite] w-1/3"></div>
          </div>
          
          {}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-start justify-between gap-6 sm:gap-8 lg:gap-12 relative z-10">
            {stages.map((stage, index) => <ProcessNode key={stage.id} stage={stage} index={index} isLast={index === stages.length - 1} />)}
          </div>
        </div>

      </div>
    </section>;
}